import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _attempt from "lodash/attempt";
import { UserEventTracker } from '@datacamp/campus-integration';
import config from '../config';
import { sentry } from './sentry';
import socketIO from './socketIO';
import { UserEventTrackerSessionIdNotSetError } from './UserEventTrackerSessionIdNotSetError';
const WEBSOCKET_TIMEOUT_DELAY_IN_MS = 4000;
export class RemoteDesktopSocketClient {
  constructor(environment, courseId, listener, onSessionId, tracker) {
    var _this = this;
    this.environment = environment;
    this.courseId = courseId;
    this.listener = listener;
    this.onSessionId = onSessionId;
    this.tracker = tracker;
    _defineProperty(this, "socket", void 0);
    _defineProperty(this, "socketStatus", {
      status: 'none'
    });
    _defineProperty(this, "sessionStatus", {
      isNewSession: undefined,
      status: 'none'
    });
    _defineProperty(this, "sessionId", void 0);
    _defineProperty(this, "connectTimeout", void 0);
    _defineProperty(this, "updateStatus", _ref => {
      let {
        message,
        status
      } = _ref;
      this.sessionStatus = {
        isNewSession: this.sessionStatus.isNewSession,
        message,
        status
      };
      if (this.isReady()) {
        if (this.sessionId === undefined) {
          const error = new UserEventTrackerSessionIdNotSetError();
          sentry.captureError(error);
        } else {
          this.tracker.eventUserResponseSessionFrontend(this.sessionId);
        }
      }
      this.fireListener();
    });
    _defineProperty(this, "onGetOrCreateSession", _ref2 => {
      let {
        id,
        isNewSession
      } = _ref2;
      this.onSessionId(id);
      this.sessionStatus.isNewSession = isNewSession;
      this.sessionId = id;
      _attempt(() => this.socket.emit('listenToStatus', {
        trackingUuid: UserEventTracker.trackingUuid
      }));
    });
    _defineProperty(this, "onConnect", function () {
      let {
        forceNew
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        forceNew: false
      };
      _this.cancelConnectTimout();
      _this.socketStatus = {
        status: 'connected'
      };
      _this.sessionStatus = {
        isNewSession: _this.sessionStatus.isNewSession,
        status: 'pending'
      };
      _this.fireListener();
      _this.tracker.eventUserRequestSessionFrontend();
      _attempt(() => _this.socket.emit('getOrCreateSession', {
        courseId: _this.courseId,
        forceNew,
        trackingUuid: UserEventTracker.trackingUuid
      }));
    });
    _defineProperty(this, "onException", e => {
      this.cancelConnectTimout();
      this.socketStatus = {
        message: e.message,
        status: e.status === 'duplicate' ? 'duplicate' : 'error'
      };
      this.sessionStatus = {
        isNewSession: undefined,
        status: 'stopped'
      };
      this.fireListener();
    });
    _defineProperty(this, "onDisconnect", () => {
      this.socketStatus = {
        status: 'disconnected'
      };
      this.sessionStatus = {
        isNewSession: undefined,
        status: 'stopped'
      };
      this.fireListener();
    });
    _defineProperty(this, "initSocketTimeout", () => {
      this.cancelConnectTimout();
      this.connectTimeout = setTimeout(this.onSocketTimeout, WEBSOCKET_TIMEOUT_DELAY_IN_MS);
    });
    _defineProperty(this, "cancelConnectTimout", () => {
      if (this.connectTimeout != null) {
        clearTimeout(this.connectTimeout);
      }
    });
    _defineProperty(this, "onSocketTimeout", () => {
      this.socketStatus = {
        status: 'error'
      };
      this.sessionStatus = {
        isNewSession: undefined,
        status: 'stopped'
      };
      this.fireListener();
    });
    this.fireListener();
    this.socket = socketIO(config(this.environment).urls.REMOTE_DESKTOP_API, {
      transports: ['websocket']
    });
    this.socket.on('connect', this.onConnect);
    this.socket.on('disconnect', this.onDisconnect);
    this.socket.on('exception', this.onException);
    this.socket.on('getOrCreateSession', this.onGetOrCreateSession);
    this.socket.on('listenToStatus', this.updateStatus);
    this.initSocketTimeout();
  }
  disconnect() {
    this.socketStatus = {
      status: 'disconnected'
    };
    this.sessionStatus = {
      isNewSession: undefined,
      status: 'stopped'
    };
    this.fireListener();
    this.socket.disconnect();
  }
  initialize(_ref3) {
    let {
      forceNew
    } = _ref3;
    this.initSocketTimeout();
    this.socketStatus = {
      status: 'none'
    };
    this.sessionStatus = {
      isNewSession: undefined,
      status: 'none'
    };
    this.fireListener();
    if (this.socket.disconnected) {
      this.socket.connect();
    } else {
      this.onConnect({
        forceNew
      });
    }
  }
  isReady() {
    return this.sessionStatus.status === 'running' && this.socketStatus.status === 'connected';
  }
  fireListener() {
    this.listener({
      session: this.sessionStatus,
      socket: this.socketStatus
    });
  }
}